import { ActionIcon, Badge, Button, Group, Menu, Paper, Stack, Text } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import {
  IconArrowBarToUp,
  IconArrowDown,
  IconArrowUp,
  IconCalendarEvent,
  IconDots,
  IconGripVertical,
  IconTrash,
} from "@tabler/icons-react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { semanticColor } from "@how/design-system";
import { announce, blockName, DRAG_INSTRUCTIONS } from "./block-order";
import { scheduleChips, type BlockScheduleValue } from "./block-schedule";

/** One block in the editor's list: a key that survives reordering (the block's id, or a local one until saved). */
export interface BlockItem {
  readonly key: string;
  readonly block: Record<string, unknown>;
}

export interface BlockListProps {
  readonly items: readonly BlockItem[];
  /** The block type's name staff recognise ("Hero"). */
  readonly labelOf: (block: Record<string, unknown>) => string;
  /** The block's headline, for its card and its announcements. */
  readonly summaryOf: (block: Record<string, unknown>) => string;
  readonly invalid: readonly boolean[];
  readonly drawable: (block: Record<string, unknown>) => boolean;
  readonly manage: boolean;
  readonly timeZone: string;
  readonly now: Date;
  readonly onEdit: (index: number) => void;
  /** A new order: a drag, an arrow, or Move to top. `focus` names the control to put focus on afterwards. */
  readonly onMove: (from: number, to: number, focus: "handle" | "up" | "down" | null) => void;
  readonly onRemove: (index: number) => void;
}

/**
 * The page's blocks as cards, in order (ADR 0031, amendment of 2026-10-03). Each card is dragged by its handle, with
 * the pointer or the keyboard, and keeps its up and down arrows and a Move to top in its menu, so every way of moving
 * a block works without a pointer. A screen reader hears each move in plain words.
 */
export function BlockList(props: BlockListProps) {
  const { items } = props;
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a click on the handle stays a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const keys = items.map((item) => item.key);
  const total = items.length;
  const indexOf = (id: UniqueIdentifier) => keys.indexOf(String(id));
  const nameOf = (id: UniqueIdentifier) => {
    const item = items[indexOf(id)];
    return item === undefined
      ? "the block"
      : blockName(props.labelOf(item.block), props.summaryOf(item.block));
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => announce.pickedUp(nameOf(active.id), indexOf(active.id), total),
    onDragOver: ({ active, over }) =>
      over === null ? undefined : announce.over(nameOf(active.id), indexOf(over.id), total),
    onDragEnd: ({ active, over }) =>
      over === null || over.id === active.id
        ? announce.droppedInPlace(nameOf(active.id), indexOf(active.id), total)
        : announce.moved(nameOf(active.id), indexOf(over.id), total),
    onDragCancel: ({ active }) => announce.cancelled(nameOf(active.id), indexOf(active.id), total),
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over === null || over.id === active.id) return;
    props.onMove(indexOf(active.id), indexOf(over.id), null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{ announcements, screenReaderInstructions: { draggable: DRAG_INSTRUCTIONS } }}
    >
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        <Stack
          gap="sm"
          component="ol"
          aria-label="Blocks on this page"
          m={0}
          p={0}
          style={{ listStyle: "none" }}
        >
          {items.map((item, index) => (
            <BlockCard key={item.key} item={item} index={index} total={total} {...props} />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function BlockCard({
  item,
  index,
  total,
  labelOf,
  summaryOf,
  invalid,
  drawable,
  manage,
  timeZone,
  now,
  onEdit,
  onMove,
  onRemove,
}: BlockListProps & { readonly item: BlockItem; readonly index: number; readonly total: number }) {
  const reduced = useReducedMotion();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.key,
    disabled: !manage,
    // Under reduced motion the other cards jump to their new places rather than slide.
    ...(reduced ? { transition: null } : {}),
  });
  const label = labelOf(item.block);
  const summary = summaryOf(item.block);
  const number = String(index + 1);
  const chips = scheduleChips(
    item.block["schedule"] as BlockScheduleValue | null | undefined,
    now,
    timeZone,
  );

  return (
    <Paper
      component="li"
      ref={setNodeRef}
      withBorder
      p="md"
      radius="md"
      data-block-key={item.key}
      data-dragging={isDragging ? "" : undefined}
      {...(isDragging ? { className: "how-floating" } : {})}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: reduced ? undefined : transition,
        position: "relative",
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          {manage ? (
            <ActionIcon
              ref={setActivatorNodeRef}
              variant="subtle"
              color={semanticColor("neutral")}
              data-control="handle"
              {...attributes}
              {...listeners}
              aria-label={`Drag to reorder block ${number}, ${label}`}
              style={{
                cursor: isDragging ? "grabbing" : "grab",
                touchAction: "none",
                flexShrink: 0,
              }}
            >
              <IconGripVertical size={16} />
            </ActionIcon>
          ) : null}
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="xs">
              <Badge variant="light" color={semanticColor("neutral")} size="md">
                {label}
              </Badge>
              {invalid[index] === true ? (
                <Badge variant="light" color={semanticColor("danger")} size="md">
                  Needs attention
                </Badge>
              ) : null}
              {chips === null ? null : (
                <Badge
                  variant="outline"
                  color={semanticColor("neutral")}
                  size="md"
                  leftSection={<IconCalendarEvent size={12} aria-hidden />}
                  tt="none"
                >
                  {chips.window}
                </Badge>
              )}
              {chips?.status == null ? null : (
                <Badge
                  variant="light"
                  color={semanticColor(chips.status === "Scheduled" ? "info" : "warning")}
                  size="md"
                  tt="none"
                >
                  {chips.status}
                </Badge>
              )}
            </Group>
            <Text size="sm" truncate>
              {summary || " "}
            </Text>
          </Stack>
        </Group>
        <Group gap={4} wrap="nowrap">
          <Button
            variant="subtle"
            size="xs"
            disabled={!drawable(item.block)}
            aria-label={`${manage ? "Edit" : "View"} block ${number}, ${label}`}
            onClick={() => {
              onEdit(index);
            }}
          >
            {manage ? "Edit" : "View"}
          </Button>
          {manage ? (
            <>
              <ActionIcon
                variant="subtle"
                aria-label={`Move block ${number} up`}
                data-control="up"
                disabled={index === 0}
                onClick={() => {
                  onMove(index, index - 1, "up");
                }}
              >
                <IconArrowUp size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                aria-label={`Move block ${number} down`}
                data-control="down"
                disabled={index === total - 1}
                onClick={() => {
                  onMove(index, index + 1, "down");
                }}
              >
                <IconArrowDown size={16} />
              </ActionIcon>
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    aria-label={`More actions for block ${number}, ${label}`}
                  >
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconArrowBarToUp size={14} />}
                    disabled={index === 0}
                    onClick={() => {
                      onMove(index, 0, "handle");
                    }}
                  >
                    Move to top
                  </Menu.Item>
                  <Menu.Item
                    color={semanticColor("danger")}
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                      onRemove(index);
                    }}
                  >
                    Remove block
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          ) : null}
        </Group>
      </Group>
    </Paper>
  );
}
